import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import {
  fetchNotifications,
  readNotification,
  readAllNotifications,
  notifBus,
  notificationLinkTarget,
  notificationTypeHint,
  notificationPriorityBadgeLabel,
} from "../../constants/notifications";
import { showError, promise } from "../../utils/toast.js";
import { acquireBodyScrollLock } from "../../utils/bodyScrollLock.js";
import { useConfirm } from "../ui/ConfirmationModal";
import { useIsMobile } from "../ui/useIsMobile.js";
import { Z_INDEX } from "../ui/zIndex.js";
import AppButton from "../ui/AppButton.jsx";
import { cn } from "../../utils/themeClasses.js";
import {
  buttonSizes,
  detailPageHeaderNavSecondaryShellClassName,
} from "../../design/tokens.js";

/**
 * Kompakte Popup-/Bottom-Sheet-Liste der letzten Benachrichtigungen.
 * - Mobile: Bottom Sheet (mit Body-Scroll-Lock)
 * - Desktop: Glass-Popup unter dem Bell-Anchor ([data-notif-anchor])
 * - Fokus-Trap + ESC schließt
 * - Live-Refresh via notifBus (soweit vorhanden)
 */
export default function NotificationList({
  open,
  onClose,
  id
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const [pos, setPos] = useState({
    top: 64,
    left: null,
    right: 16
  });
  const isMobile = useIsMobile();
  const mountedRef = useRef(true);
  const loadReqRef = useRef(0);
  const headerId = useMemo(() => `${id || "notif-panel"}-label`, [id]);
  const [markingAll, setMarkingAll] = useState(false);
  const unreadExists = useMemo(() => items.some(i => !i.is_read), [items]);
  const confirm = useConfirm();
  const rtf = useMemo(() => new Intl.RelativeTimeFormat("de-CH", { numeric: "auto" }), []);
  const relTime = useCallback(
    iso => {
      try {
        const d = new Date(iso);
        const diffMin = Math.round((d.getTime() - Date.now()) / 60000);
        if (Math.abs(diffMin) < 60) return rtf.format(diffMin, "minute");
        const diffH = Math.round(diffMin / 60);
        if (Math.abs(diffH) < 24) return rtf.format(diffH, "hour");
        const diffD = Math.round(diffH / 24);
        return rtf.format(diffD, "day");
      } catch {
        return "";
      }
    },
    [rtf]
  );

  /* ───────────────── Daten ───────────────── */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const load = useCallback(async () => {
    const reqId = ++loadReqRef.current;
    setLoading(true);
    try {
      const {
        rows
      } = await fetchNotifications({
        limit: 20
      });
      if (!mountedRef.current || reqId !== loadReqRef.current) return;
      setItems(Array.isArray(rows) ? rows : []);
    } finally {
      if (mountedRef.current && reqId === loadReqRef.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Live-Updates vom Badge-Bus (read-one / read-all / mark-related)
  useEffect(() => {
    if (!open) return;
    const handler = () => load();
    const unsub = notifBus.on?.("changed", handler);
    return () => {
      if (typeof unsub === "function") unsub();
    };
  }, [open, load]);

  /* ───────────────── Body-Scroll sperren (Mobile) ───────────────── */
  useEffect(() => {
    if (!open || !isMobile) return;
    const release = acquireBodyScrollLock();
    return () => release();
  }, [open, isMobile]);

  /* ───────────────── Position (Desktop) ───────────────── */
  useLayoutEffect(() => {
    if (!open || isMobile) return;
    const anchor = document.querySelector("[data-notif-anchor]");
    let top = 64,
      left = null,
      right = 16;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      top = Math.max(12, r.bottom + 8);
      const vw = window.innerWidth;
      right = Math.max(8, vw - r.right + 4);
      left = null;
    }
    setPos({
      top,
      left,
      right
    });
  }, [open, isMobile]);

  // bei Resize/Scroll leicht neu positionieren
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    const onRecalc = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setPos(p => ({
        ...p
      })));
    };
    window.addEventListener("resize", onRecalc);
    window.addEventListener("scroll", onRecalc, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc, true);
    };
  }, [open]);

  /* ───────────────── Fokus-Management (Trap + Return) ───────────────── */
  useEffect(() => {
    if (!open) return;
    lastFocusedRef.current = document.activeElement;
    const el = panelRef.current;
    queueMicrotask(() => el?.focus?.());
    const handleKeyDown = e => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
      if (e.key === "Tab") {
        const focusables = el.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (!focusables.length) {
          e.preventDefault();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      lastFocusedRef.current && lastFocusedRef.current.focus?.();
    };
  }, [open, onClose]);
  if (!open) return null;

  /* ───────────────── Aktionen ───────────────── */
  const onItemClick = async n => {
    // Optimistic UI
    setItems(prev => prev.map(x => x.id === n.id ? {
      ...x,
      is_read: true
    } : x));
    try {
      await readNotification(n.id);
    } catch (e) {
      // Rollback bei Fehler
      if (!mountedRef.current) return;
      setItems(prev => prev.map(x => x.id === n.id ? {
        ...x,
        is_read: false
      } : x));
      showError("Konnte Benachrichtigung nicht markieren.");
    }
  };
  const onReadAll = async () => {
    if (markingAll || !unreadExists) return;

    // 🔒 Projektweites Bestätigungs-Modal
    const res = await confirm({
      title: "Alle als gelesen markieren?",
      message: "Die letzten Benachrichtigungen werden als gelesen markiert. Diese Aktion ist reversibel, neue Meldungen kommen wie gewohnt rein.",
      confirmLabel: "Ja, markieren",
      cancelLabel: "Abbrechen",
      variant: "default",
      destructive: false,
      escCloses: true,
      closeOnOverlay: true
    });
    if (!res?.ok) return;
    setMarkingAll(true);
    // Optimistic UI
    setItems(prev => prev.map(x => ({
      ...x,
      is_read: true
    })));
    const p = readAllNotifications();
    promise(p, {
      loading: "Markiere alle…",
      success: "Alles gelesen ✅",
      error: "Fehler beim Markieren"
    });
    try {
      await p;
    } catch (e) {
      // Rollback bei Fehler
      await load();
    } finally {
      if (mountedRef.current) setMarkingAll(false);
    }
  };

  /* ───────────────── UI ───────────────── */
  const Container = <div className="fixed inset-0" style={{
    zIndex: Z_INDEX.modal
  }} onClick={onClose} aria-hidden>
      {/* Scrim */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />

      {/* Panel — Shell wie Login/Auth-Karte */}
      <div id={id} ref={panelRef} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby={headerId} tabIndex={-1} className={cn("outline-none transition-[transform,opacity] duration-200 motion-reduce:transition-none text-white", "backdrop-blur-xl bg-white/[0.03] border border-white/[0.08]", "shadow-[0_32px_80px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.05)]", isMobile ? "fixed left-0 right-0 mx-auto w-full max-w-full rounded-b-none rounded-t-[28px]" : "absolute rounded-[28px] w-[min(30rem,calc(100vw-1.5rem))]")} style={isMobile ? {
      bottom: 0,
      paddingTop: "max(env(safe-area-inset-top,8px),8px)",
      maxHeight: "min(80vh, calc(100vh - (env(safe-area-inset-top,0px)) - 24px))"
    } : {
      top: pos.top,
      right: pos.right ?? undefined,
      left: pos.left ?? undefined
    }}>
        {/* Header */}
        <div className={cn("px-3 py-2.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 border-b border-white/[0.08] relative", isMobile ? "pt-6 pb-2.5" : "pt-3")}
        >
          {isMobile && <div className="absolute left-1/2 -translate-x-1/2 top-2 h-1.5 w-10 rounded-full bg-white/20" />}
          <div id={headerId} className="font-semibold text-white flex items-center gap-2 min-w-0 flex-1 basis-[min(100%,12rem)] text-sm sm:text-[15px]">
            <span
              className="flex h-8 w-8 sm:h-9 sm:w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.05] border border-white/[0.09]"
              aria-hidden
            >
              <Bell className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-amber-400/90" strokeWidth={2.25} />
            </span>
            <span className="min-w-0 text-wrap leading-snug [overflow-wrap:anywhere]">Benachrichtigungen</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-auto">
            <AppButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={onReadAll}
              disabled={!unreadExists || markingAll}
              className={cn(
                detailPageHeaderNavSecondaryShellClassName,
                "!border-default hover:!border-default",
                "!min-h-0 h-8 px-2.5 text-[11px] font-normal leading-tight",
                !unreadExists || markingAll ? "opacity-50 cursor-not-allowed" : ""
              )}
            >
              {markingAll ? "Markiere…" : "Alle als gelesen"}
            </AppButton>
            <Link
              to="/benachrichtigungen"
              onClick={onClose}
              className={cn(
                "wrtk-btn inline-flex items-center justify-center gap-1.5 rounded-lg border font-semibold transition-colors duration-150 focus-ring btn-primary no-underline",
                buttonSizes.sm,
                "!min-h-0 h-8 px-2.5 text-[11px] leading-tight"
              )}
            >
              Alle anzeigen
            </Link>
          </div>
        </div>

        {/* Liste */}
        <div className="px-2 py-2 max-h-[72vh] sm:max-h-[28rem] overflow-y-auto overscroll-contain scrollbar-thin space-y-1.5" role="list" aria-label="Benachrichtigungen Liste">
          {loading && <div className="p-4 text-sm text-white/55">
              Laden…
            </div>}
          {!loading && items.length === 0 && <div className="px-4 py-6 text-sm text-white/55">
              Keine Benachrichtigungen.
            </div>}

          {items.map(n => {
            const bracketLine = (() => {
              const parts = [];
              if (n?.meta?.name) parts.push(n.meta.name);
              if (n?.meta?.customer_label) parts.push(n.meta.customer_label);
              if (n?.meta?.object_label) parts.push(n.meta.object_label);
              return parts.length ? parts.join(" — ") : null;
            })();
            const typeHint = notificationTypeHint(n.type);
            const subLine = [typeHint, bracketLine].filter(Boolean).join(" · ") || null;
            const openTarget = notificationLinkTarget(n.link);
            const pri = n?.priority || "info";
            const priBadge = notificationPriorityBadgeLabel(pri);
            const evc = Number(n?.event_count) > 1 ? n.event_count : null;
            const timeStr = relTime(n.created_at);
            const openAria = `${String(n.title || "Benachrichtigung").slice(0, 120)}${openTarget ? " öffnen" : ""}`;

            const cardClass = cn(
              "w-full text-left px-3 py-3 transition-colors rounded-xl min-h-[44px] h-auto font-normal border border-default shadow-none",
              openTarget
                ? "cursor-pointer no-underline text-inherit block focus-ring outline-offset-2"
                : "focus-ring outline-offset-2",
              n.is_read
                ? "bg-white/[0.02] hover:bg-white/[0.06]"
                : pri === "action"
                  ? "bg-amber-500/[0.14] hover:bg-amber-500/[0.2]"
                  : pri === "warning"
                    ? "bg-orange-500/[0.12] hover:bg-orange-500/[0.18]"
                    : "bg-blue-500/[0.12] hover:bg-blue-500/[0.18]"
            );

            const rowInner = (
              <div className="flex items-start gap-3">
                <span
                  className={cn(
                    "mt-1.5 inline-block w-2 h-2 shrink-0 rounded-full",
                    n.is_read ? "bg-white/25" : "bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.55)]"
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-medium text-white/95 [overflow-wrap:anywhere]">{n.title}</div>
                    {priBadge && (
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-default text-white/50 shrink-0">
                        {priBadge}
                      </span>
                    )}
                    {evc != null && <span className="text-[10px] text-white/45 shrink-0">×{evc}</span>}
                  </div>
                  {subLine && (
                    <div className="text-xs mt-0.5 text-white/45 [overflow-wrap:anywhere]">{subLine}</div>
                  )}
                  {n.body && (
                    <div className="text-xs mt-1 text-white/60 leading-snug [overflow-wrap:anywhere] line-clamp-4">
                      {n.body}
                    </div>
                  )}
                </div>
                {!!timeStr && (
                  <div className="shrink-0 text-[10px] text-white/50 text-right pt-0.5 max-w-[5.5rem] leading-tight whitespace-nowrap">
                    {timeStr}
                  </div>
                )}
              </div>
            );

            if (openTarget?.type === "router") {
              return (
                <Link
                  key={n.id}
                  role="listitem"
                  to={openTarget.to}
                  title="Zum Eintrag wechseln"
                  aria-label={openAria}
                  onClick={() => {
                    void onItemClick(n);
                    onClose?.();
                  }}
                  onAuxClick={e => {
                    if (e.button === 1) void onItemClick(n);
                  }}
                  className={cardClass}
                >
                  {rowInner}
                </Link>
              );
            }
            if (openTarget?.type === "external") {
              return (
                <a
                  key={n.id}
                  role="listitem"
                  href={openTarget.href}
                  title="In neuem Tab öffnen"
                  aria-label={openAria}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    void onItemClick(n);
                    onClose?.();
                  }}
                  onAuxClick={e => {
                    if (e.button === 1) void onItemClick(n);
                  }}
                  className={cardClass}
                >
                  {rowInner}
                </a>
              );
            }
            return (
              <AppButton
                key={n.id}
                type="button"
                variant="ghost"
                role="listitem"
                onClick={() => onItemClick(n)}
                aria-label={openAria}
                className={cn(cardClass, "!justify-start")}
              >
                {rowInner}
              </AppButton>
            );
          })}
        </div>

        {/* Safe-Area unten */}
        {isMobile && <div style={{
        height: "max(env(safe-area-inset-bottom, 8px), 8px)"
      }} />}
      </div>
    </div>;
  return createPortal(Container, document.body);
}